import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ClipboardList, Plane, CalendarDays, Clock,
  ChevronLeft, ChevronRight, ArrowUpRight, Sparkles, TriangleAlert, Rocket,
} from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useBreakpoint } from "@/lib/useBreakpoint";
import { WS_TOKENS } from "@/lib/workspaceTheme";
import { fmtNum } from "@/lib/format";

/**
 * DashboardPage v4 — Apple HIG inspired "Today" screen.
 *
 *   1. Large greeting with subtle gold accent.
 *   2. Three vivid KPI tiles (icon + big monospaced number + label).
 *   3. Primary + secondary CTAs (pill shaped).
 *   4. "Priority now" grouped list (rows with avatars + semantic pills).
 */
export default function DashboardPage() {
  const { user } = useAuth();
  const { theme, lang, dir } = useI18n();
  const { isDesktop } = useBreakpoint();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [priority, setPriority] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const [k, apps] = await Promise.all([
          api.get("/workspace/kpis/me").then((r) => r.data).catch(() => null),
          api.get("/workspace/applications", { params: { view: "action_required", limit: 8 } })
            .then((r) => r.data).catch(() => null),
        ]);
        if (!alive) return;
        setKpis(k || {});
        setPriority((apps?.items || []).slice(0, 6));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user) return null;

  const isRoot = user.role?.is_root;
  const roleCode = user.role?.code;
  const canCreate = isRoot || ["employee", "general_manager", "deputy_manager"].includes(roleCode);
  const shortName = (user.full_name || user.username || "").trim().split(/\s+/)[0];
  const greeting = timeGreeting(lang);

  const kpiTiles = [
    {
      key: "action", tone: "gold",
      icon: Rocket,
      count: kpis?.assigned_to_me ?? 0,
      label: lang === "ar" ? "بانتظار إجرائك" : "Awaiting your action",
      route: "/applications",
    },
    {
      key: "travel", tone: "danger",
      icon: TriangleAlert,
      count: kpis?.upcoming_travel ?? 0,
      label: lang === "ar" ? "موعد خلال 10 أيام" : "Travel ≤ 10 days",
      route: "/applications?view=travel_soon",
    },
    {
      key: "new", tone: "success",
      icon: Sparkles,
      count: kpis?.new_applications ?? 0,
      label: lang === "ar" ? "معاملات جديدة" : "New applications",
      route: "/applications?view=new",
    },
  ];

  const toneMap = {
    gold:    { color: theme.gold,    bg: "rgba(212,175,55,0.10)", border: "rgba(212,175,55,0.28)", glow: "rgba(212,175,55,0.25)" },
    danger:  { color: theme.danger,  bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.28)",  glow: "rgba(220,38,38,0.22)" },
    success: { color: theme.success, bg: "rgba(22,163,74,0.10)",  border: "rgba(22,163,74,0.28)",  glow: "rgba(22,163,74,0.20)" },
  };

  const css = {
    page: {
      minHeight: "100%",
      background: theme.bg, color: theme.text,
      padding: `${isDesktop ? 36 : 20}px ${isDesktop ? 36 : 16}px 40px`,
      fontFamily: "'Tajawal','Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
      maxWidth: 1200, margin: "0 auto",
    },
    // ── Big greeting ────────────────────────────────────────────
    helloRow: { marginBottom: 8, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
    hello: {
      fontSize: isDesktop ? 34 : 26, fontWeight: 800, color: theme.text,
      letterSpacing: "-0.6px", lineHeight: 1.1,
    },
    helloAccent: {
      color: theme.gold,
      fontWeight: 900,
    },
    subLine: {
      fontSize: isDesktop ? 15 : 13, color: theme.textMuted,
      marginBottom: 26, lineHeight: 1.6, fontWeight: 500,
    },
    // ── KPI tiles ───────────────────────────────────────────────
    kpiRow: {
      display: "grid",
      gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "repeat(3, 1fr)",
      gap: isDesktop ? 12 : 8, marginBottom: 20,
    },
    kpiTile: (tone) => ({
      position: "relative",
      background: theme.surface,
      border: `1px solid ${theme.borderSoft}`,
      borderRadius: isDesktop ? 18 : 14,
      padding: isDesktop ? "20px 22px" : "12px 12px",
      cursor: "pointer",
      overflow: "hidden",
      textAlign: "start", fontFamily: "inherit",
      display: "flex", flexDirection: "column", gap: isDesktop ? 6 : 4,
      transition: `transform ${WS_TOKENS.motion.base}, box-shadow ${WS_TOKENS.motion.base}, border-color ${WS_TOKENS.motion.base}`,
      boxShadow: theme.cardShadow,
    }),
    kpiIconWrap: (tone) => ({
      width: isDesktop ? 40 : 30, height: isDesktop ? 40 : 30, borderRadius: isDesktop ? 12 : 9,
      background: toneMap[tone].bg,
      border: `1px solid ${toneMap[tone].border}`,
      color: toneMap[tone].color,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      marginBottom: isDesktop ? 8 : 2,
    }),
    kpiVal: (tone) => ({
      fontSize: isDesktop ? 34 : 24, fontWeight: 900, color: toneMap[tone].color,
      fontFamily: "SF Mono, ui-monospace, monospace",
      lineHeight: 1, letterSpacing: "-1px",
    }),
    kpiLbl: { fontSize: isDesktop ? 13 : 10.5, color: theme.textMuted, fontWeight: 700, lineHeight: 1.3 },
    kpiArrow: (tone) => ({
      position: "absolute",
      top: isDesktop ? 16 : 8, insetInlineEnd: isDesktop ? 16 : 8,
      color: toneMap[tone].color, opacity: 0.5,
    }),
    // ── CTA row ─────────────────────────────────────────────────
    ctaRow: { display: "flex", gap: 10, marginBottom: 28, flexWrap: "wrap" },
    ctaPrimary: {
      background: theme.goldGradient, color: "#0A0F19", border: "none",
      borderRadius: 999, padding: "12px 22px",
      fontWeight: 800, fontSize: 14, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 8,
      minHeight: 44, fontFamily: "inherit",
      boxShadow: "0 6px 18px rgba(212,175,55,0.28)",
      transition: `transform ${WS_TOKENS.motion.fast}, box-shadow ${WS_TOKENS.motion.fast}`,
    },
    ctaSecondary: {
      background: theme.surface, color: theme.text,
      border: `1px solid ${theme.border}`,
      borderRadius: 999, padding: "12px 20px",
      fontWeight: 700, fontSize: 14, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 8,
      minHeight: 44, fontFamily: "inherit",
      transition: `background ${WS_TOKENS.motion.fast}, border-color ${WS_TOKENS.motion.fast}`,
    },
    // ── Priority section ────────────────────────────────────────
    section: {
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 18, overflow: "hidden", boxShadow: theme.cardShadow,
    },
    sectionHead: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "16px 20px", borderBottom: `1px solid ${theme.borderSoft}`,
    },
    sectionTitle: { fontSize: 15, fontWeight: 800, color: theme.text, letterSpacing: "-0.2px" },
    sectionMore: {
      background: "transparent", border: "none",
      color: theme.gold, fontSize: 13, fontWeight: 700,
      cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
      fontFamily: "inherit",
    },
    row: {
      display: "flex", alignItems: "center", gap: 14,
      padding: "14px 20px", borderBottom: `1px solid ${theme.borderSoft}`,
      cursor: "pointer",
      transition: `background ${WS_TOKENS.motion.fast}`,
      width: "100%", border: 0,
      borderBlockEnd: `1px solid ${theme.borderSoft}`,
      background: "transparent", fontFamily: "inherit", color: "inherit", textAlign: "start",
    },
    empty: { padding: 40, textAlign: "center", color: theme.textMuted, fontSize: 13 },
    skRow: {
      height: 66,
      background: `linear-gradient(90deg, transparent, ${theme.borderSoft}, transparent)`,
      backgroundSize: "200% 100%",
      animation: "ws-shimmer 1.4s infinite",
      borderBottom: `1px solid ${theme.borderSoft}`,
    },
  };

  const ChevronIcon = dir === "rtl" ? ChevronLeft : ChevronRight;

  return (
    <div style={css.page} dir={dir} data-testid="dashboard-page">
      <style>{`
        @keyframes ws-shimmer { 0%{background-position:0 0} 100%{background-position:-200% 0} }
        @keyframes ws-tile-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .ws-kpi-tile { animation: ws-tile-in 340ms cubic-bezier(.2,.8,.2,1) both; }
        .ws-kpi-tile:hover { transform: translateY(-2px); border-color: ${theme.borderStrong} !important; }
        .ws-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(212,175,55,0.36) !important; }
        .ws-cta-secondary:hover { background: ${theme.surfaceElevated} !important; border-color: ${theme.borderStrong} !important; }
        .ws-prio-row:hover { background: ${theme.name === "dark" ? "rgba(255,255,255,0.035)" : "rgba(0,0,0,0.025)"} !important; }
      `}</style>

      <div style={css.helloRow} data-testid="dashboard-hello">
        <div style={css.hello}>
          {greeting}{shortName ? <> ، <span style={css.helloAccent}>{shortName}</span></> : ""}
        </div>
      </div>
      <div style={css.subLine} data-testid="dashboard-summary-line">
        {buildSummaryLine(kpis, lang)}
      </div>

      <div style={css.kpiRow} data-testid="dashboard-summary">
        {kpiTiles.map((t, i) => (
          <button
            key={t.key}
            type="button"
            className="ws-kpi-tile"
            style={{ ...css.kpiTile(t.tone), animationDelay: `${i * 60}ms` }}
            onClick={() => navigate(t.route)}
            data-testid={`dashboard-summary-${t.tone}`}
          >
            <div style={css.kpiIconWrap(t.tone)}>
              <t.icon size={isDesktop ? 20 : 16} strokeWidth={2} />
            </div>
            <span style={css.kpiVal(t.tone)}>{fmtNum(t.count)}</span>
            <span style={css.kpiLbl}>{t.label}</span>
            <ArrowUpRight size={16} strokeWidth={2} style={css.kpiArrow(t.tone)} />
          </button>
        ))}
      </div>

      <div style={css.ctaRow}>
        {canCreate && (
          <button
            type="button"
            onClick={() => navigate("/applications/new")}
            className="ws-cta-primary"
            style={css.ctaPrimary}
            data-testid="dashboard-new-application"
          >
            <Plus size={18} strokeWidth={2.4} />
            {lang === "ar" ? "معاملة جديدة" : "New Application"}
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate("/applications")}
          className="ws-cta-secondary"
          style={css.ctaSecondary}
          data-testid="dashboard-open-applications"
        >
          <ClipboardList size={18} strokeWidth={1.75} />
          {lang === "ar" ? "كل المعاملات" : "All Applications"}
        </button>
      </div>

      <section style={css.section} data-testid="dashboard-priority">
        <div style={css.sectionHead}>
          <div style={css.sectionTitle}>{lang === "ar" ? "الأولوية الآن" : "Priority now"}</div>
          <button type="button" style={css.sectionMore} onClick={() => navigate("/applications")} data-testid="dashboard-priority-more">
            {lang === "ar" ? "عرض الكل" : "View all"}
            <ChevronIcon size={14} strokeWidth={2.4} />
          </button>
        </div>

        {loading ? (
          [0, 1, 2].map((i) => <div key={i} style={css.skRow} />)
        ) : priority.length === 0 ? (
          <div style={css.empty} data-testid="dashboard-priority-empty">
            {lang === "ar" ? "لا توجد معاملات تتطلب إجراءً منك الآن" : "No applications need your action right now"}
          </div>
        ) : (
          priority.map((app, i) => (
            <PriorityRow
              key={app.id} app={app} css={css} lang={lang} theme={theme}
              onOpen={() => navigate(`/applications/${app.id}`)}
              ChevronIcon={ChevronIcon} isLast={i === priority.length - 1}
            />
          ))
        )}
      </section>
    </div>
  );
}

function timeGreeting(lang) {
  const h = new Date().getHours();
  if (lang === "ar") {
    if (h < 12) return "صباح الخير";
    if (h < 18) return "طاب مساؤك";
    return "مساء الخير";
  }
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function buildSummaryLine(k, lang) {
  const a = k?.assigned_to_me ?? 0;
  const t = k?.upcoming_travel ?? 0;
  const n = k?.new_applications ?? 0;
  if (a === 0 && t === 0 && n === 0) {
    return lang === "ar" ? "لا يوجد ما يستدعي إجراءك اليوم." : "Nothing needs your action today.";
  }
  if (lang === "ar") {
    return `لديك ${fmtNum(a)} بحاجة لإجراء${t ? `، و ${fmtNum(t)} موعد خلال 10 أيام` : ""}${n ? `، و ${fmtNum(n)} جديدة` : ""}.`;
  }
  return `You have ${fmtNum(a)} awaiting action${t ? `, ${fmtNum(t)} appointments in ≤10 days` : ""}${n ? `, ${fmtNum(n)} new` : ""}.`;
}

function PriorityRow({ app, css, lang, onOpen, ChevronIcon, theme, isLast }) {
  const status = (app.current_status_code || "").replace(/_/g, " ");
  const daysToAppointment = app.appointment_date
    ? Math.round((new Date(app.appointment_date).getTime() - Date.now()) / 86400000)
    : null;
  const isUrgent = daysToAppointment !== null && daysToAppointment <= 10;
  const source = app.is_office_submission
    ? (lang === "ar" ? "داخلي" : "Office")
    : (lang === "ar" ? "من البوابة" : "Portal");

  const name = app.client?.full_name || app.applicant_name || app.client_full_name || app.applicant?.full_name || "—";
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  const avatarPalette = [theme.info, theme.gold, theme.success, "#8B5CF6", "#EC4899", "#14B8A6"];
  const hash = Array.from(name).reduce((s, c) => s + c.charCodeAt(0), 0);
  const avatarColor = avatarPalette[hash % avatarPalette.length];

  return (
    <button
      className="ws-prio-row"
      style={{ ...css.row, borderBottom: isLast ? "none" : `1px solid ${theme.borderSoft}` }}
      onClick={onOpen}
      data-testid={`priority-row-${app.application_number || app.id}`}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 11,
        background: `linear-gradient(135deg, ${avatarColor}CC, ${avatarColor}88)`,
        color: "#fff", fontWeight: 900, fontSize: 14,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, letterSpacing: 0.5,
        boxShadow: `0 3px 10px ${avatarColor}33`,
      }}>{initial}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 3,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          letterSpacing: "-0.1px",
        }}>
          {name}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
          <span dir="ltr" style={{ fontFamily: "SF Mono, ui-monospace, monospace", fontWeight: 700 }}>
            {app.application_number || app.id?.slice(0, 8)}
          </span>
          {" · "}{source}{" · "}
          <span style={{ textTransform: "capitalize" }}>{status}</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
        {isUrgent && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(220,38,38,0.12)", color: theme.danger,
            fontSize: 11, fontWeight: 800,
            border: "1px solid rgba(220,38,38,0.22)",
          }}>
            <Plane size={11} strokeWidth={2} />
            {lang === "ar" ? `${fmtNum(daysToAppointment)} يوم` : `${fmtNum(daysToAppointment)}d`}
          </span>
        )}
        {app.appointment_date && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(59,130,246,0.12)", color: theme.info,
            fontSize: 11, fontWeight: 700,
            border: "1px solid rgba(59,130,246,0.22)",
          }}>
            <CalendarDays size={11} strokeWidth={2} />
            {lang === "ar" ? "موعد" : "Appt"}
          </span>
        )}
        {app.sla_state === "breached" && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "4px 10px", borderRadius: 999,
            background: "rgba(245,158,11,0.14)", color: theme.warning,
            fontSize: 11, fontWeight: 800,
            border: "1px solid rgba(245,158,11,0.28)",
          }}>
            <Clock size={11} strokeWidth={2} />
            SLA
          </span>
        )}
        <ChevronIcon size={16} style={{ color: theme.textMuted, opacity: 0.6 }} />
      </div>
    </button>
  );
}
